import React, { useState } from 'react';
import { Upload, Typography, Tag, Space, Alert } from 'antd';
import { InboxOutlined, CheckCircleOutlined, PaperClipOutlined } from '@ant-design/icons';
import type { UploadFile } from 'antd';
import { uploadFiles } from '../../../../../../api/file/fileServiceApi';
import type { UserUuid } from '../../../../../../types/identity';

const { Text } = Typography;

interface Props {
  taskConfig: Record<string, any>;
  patientId: UserUuid;
  userId: UserUuid;
  onChange: (payload: Record<string, any>, isValid: boolean) => void;
}

const UploadResourceTaskForm: React.FC<Props> = ({ taskConfig, patientId, userId, onChange }) => {
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [uploadedIds, setUploadedIds] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const documentCategory = taskConfig.document_category ?? 'general';

  const handleUpload = async (file: File) => {
    setUploading(true);
    setError(null);
    try {
      const result = await uploadFiles([file], patientId);
      const newIds = (result?.files ?? []).map((f: any) => f.id ?? f.file_id).filter(Boolean);
      const allIds = [...uploadedIds, ...newIds];
      setUploadedIds(allIds);
      onChange({ file_ids: allIds, document_category: documentCategory }, allIds.length > 0);
    } catch {
      setError('Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
    return false;
  };

  return (
    <Space direction="vertical" style={{ width: '100%' }} size={6}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <Text style={{ fontSize: 11, color: 'var(--mg-text-secondary)' }}>Category:</Text>
        <Tag style={{ margin: 0, fontSize: 11, padding: '0 5px' }}>{documentCategory}</Tag>
      </div>

      {error && <Alert type="error" message={error} showIcon style={{ padding: '4px 8px', fontSize: 11 }} />}

      <Upload.Dragger
        multiple
        fileList={fileList}
        beforeUpload={(file) => {
          setFileList((prev) => [...prev, { uid: file.name, name: file.name, status: 'uploading' } as UploadFile]);
          handleUpload(file);
          return false;
        }}
        showUploadList={{ showRemoveIcon: false }}
        disabled={uploading}
        style={{ padding: '8px 0' }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, padding: '4px 0' }}>
          <InboxOutlined style={{ fontSize: 20, color: 'var(--mg-text-disabled)' }} />
          <Text style={{ fontSize: 11, color: 'var(--mg-text-secondary)' }}>
            {uploading ? 'Uploading…' : 'Click or drag files to upload'}
          </Text>
        </div>
      </Upload.Dragger>

      {fileList.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {fileList.map((f) => (
            <div key={f.uid} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <PaperClipOutlined style={{ fontSize: 11, color: 'var(--mg-text-secondary)' }} />
              <Text style={{ fontSize: 11, color: 'var(--mg-text-secondary)' }}>{f.name}</Text>
              {uploadedIds.length > 0 && (
                <CheckCircleOutlined style={{ fontSize: 11, color: 'var(--mg-success)' }} />
              )}
            </div>
          ))}
        </div>
      )}

      {uploadedIds.length > 0 && (
        <Alert
          type="success"
          icon={<CheckCircleOutlined />}
          message={`${uploadedIds.length} file${uploadedIds.length > 1 ? 's' : ''} uploaded`}
          showIcon
          style={{ padding: '4px 8px', fontSize: 11 }}
        />
      )}
    </Space>
  );
};

export default UploadResourceTaskForm;
